import { useGetWorkspaceById } from "@/api/workspace";
import { defineStore } from "pinia";
import { useSessionStore } from "./session.store";
import { computed, ref } from "vue";
import {
  PermissionHelper,
  type PermissionGroup,
  type PermissionResult
} from "@territoriesai/types/lib/helpers/permission.helper.mjs";
import { useGetBillingPlanById } from "@/api/billing-plan";

export interface PermissionConfig {
  resource: string;
  operation: string;
  currentResourceCount?: number;
  requestedResourceCount?: number;
  creditsRequired?: number;
}

export const usePermissionStore = defineStore("permission", () => {
  const sessionStore = useSessionStore();
  const workspaceId = ref(
    sessionStore.selectedSession()?.workspaceUserGroup.workspaceId as string
  );
  const workspaceUserGroup = sessionStore.selectedSession()?.workspaceUserGroup;
  const { data: currentWorkspaceData, isFetching } =
    useGetWorkspaceById(workspaceId);
  const billingPlanId = computed(
    () => currentWorkspaceData.value?.workspace.currentBillingPlanId as string
  );
  const { data: billingPlan } = useGetBillingPlanById(billingPlanId);

  const hasPermission = (
    config: PermissionConfig | PermissionConfig[]
  ): PermissionResult => {
    if (Array.isArray(config)) {
      const results = config.map((cfg) =>
        PermissionHelper.hasPermission(
          workspaceUserGroup?.group as PermissionGroup,
          cfg.resource,
          cfg.operation,
          billingPlan.value,
          cfg.currentResourceCount,
          cfg.requestedResourceCount,
          currentWorkspaceData.value?.workspace,
          cfg.creditsRequired
        )
      );

      const allowed = results.every((result) => result.allowed);
      return {
        allowed,
        denyCategory: results.find((result) => !result.allowed)?.denyCategory,
        denyReason: results.find((result) => !result.denyReason)?.denyReason
      } as PermissionResult;
    }
    return PermissionHelper.hasPermission(
      workspaceUserGroup?.group as PermissionGroup,
      config.resource,
      config.operation,
      billingPlan.value,
      config.currentResourceCount,
      config.requestedResourceCount,
      currentWorkspaceData.value?.workspace,
      config.creditsRequired
    );
  };

  return {
    hasPermission,
    isFetching
  };
});
